import React, { useState, useEffect, useCallback } from 'react';
import api from '../../utils/api';

const FUEL_TYPES = ['All', 'Petrol', 'Diesel', 'CNG', 'Electric', 'Hybrid', 'Other'];

const emptyEntry = () => ({ make: '', model: '', yearFrom: '', yearTo: '', variant: 'All', fuelType: 'All' });

/**
 * VehicleCompatibilitySelector
 * Used when adding/editing a product.
 * Allows selecting multiple make/model/yearFrom-yearTo/variant/fuelType combinations.
 * 
 * Props:
 *   value: array of compatibility objects
 *   onChange: (newArray) => void
 */
const VehicleCompatibilitySelector = ({ value = [], onChange }) => {
  const [makes, setMakes] = useState([]);
  const [rowData, setRowData] = useState({}); // { rowIndex: { models, years, variants } }

  useEffect(() => {
    api.get('/vehicles/makes').then(r => setMakes(r.data?.data || [])).catch(() => {});
  }, []);

  const fetchDropdowns = useCallback(async (index, make, model) => {
    const updates = {};
    if (make) {
      const modRes = await api.get('/vehicles/models', { params: { make } }).catch(() => ({ data: { data: [] } }));
      updates.models = modRes.data?.data || [];
    }
    if (make && model) {
      const [yrRes, vrRes] = await Promise.all([
        api.get('/vehicles/years', { params: { make, model } }).catch(() => ({ data: { data: [] } })),
        api.get('/vehicles/variants', { params: { make, model } }).catch(() => ({ data: { data: [] } }))
      ]);
      updates.years = yrRes.data?.data || [];
      updates.variants = vrRes.data?.data || [];
    }
    setRowData(prev => ({ ...prev, [index]: { ...(prev[index] || {}), ...updates } }));
  }, []);

  const addRow = () => {
    onChange([...value, emptyEntry()]);
  };

  const removeRow = (index) => {
    const updated = value.filter((_, i) => i !== index);
    onChange(updated);
    setRowData(prev => {
      const copy = { ...prev };
      delete copy[index];
      return copy;
    });
  };

  const updateRow = async (index, field, val) => {
    const updated = value.map((row, i) => {
      if (i !== index) return row;
      const newRow = { ...row, [field]: val };
      if (field === 'make') { newRow.model = ''; newRow.yearFrom = ''; newRow.yearTo = ''; newRow.variant = 'All'; }
      if (field === 'model') { newRow.yearFrom = ''; newRow.yearTo = ''; newRow.variant = 'All'; }
      return newRow;
    });
    onChange(updated);

    if (field === 'make') {
      setRowData(prev => ({ ...prev, [index]: { models: [], years: [], variants: [] } }));
      fetchDropdowns(index, val, '');
    }
    if (field === 'model') {
      const make = value[index].make;
      fetchDropdowns(index, make, val);
    }
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <p className="text-sm font-semibold text-gray-700">Vehicle Compatibility</p>
        <span className="text-xs text-gray-400">Leave empty for Universal parts</span>
      </div>

      {value.length === 0 && (
        <div className="text-xs text-gray-500 bg-yellow-50 border border-yellow-200 rounded-lg px-3 py-2">
          No vehicle selected — this product will be shown as <strong>Universal</strong> (compatible with all vehicles).
        </div>
      )}

      {value.map((row, index) => {
        const rd = rowData[index] || {};
        const modelList = rd.models || [];
        const yearList = rd.years || [];
        const variantList = rd.variants || [];

        return (
          <div key={index} className="border border-gray-200 rounded-xl p-3 bg-gray-50 space-y-2 relative">
            <button
              type="button"
              onClick={() => removeRow(index)}
              className="absolute top-2 right-2 text-gray-400 hover:text-red-500 transition text-base"
              title="Remove"
            >✕</button>
            <p className="text-xs font-medium text-gray-500">Entry #{index + 1}</p>

            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
              {/* Make */}
              <div>
                <label className="text-xs text-gray-500">Make *</label>
                <select value={row.make} onChange={e => updateRow(index, 'make', e.target.value)}
                  className="w-full mt-0.5 border border-gray-300 rounded-lg px-2 py-1.5 text-sm focus:ring-1 focus:ring-red-400 focus:outline-none">
                  <option value="">Select Make</option>
                  {makes.map(m => <option key={m.value} value={m.value}>{m.label}</option>)}
                </select>
              </div>

              {/* Model */}
              <div>
                <label className="text-xs text-gray-500">Model *</label>
                <select value={row.model} onChange={e => updateRow(index, 'model', e.target.value)}
                  disabled={!row.make}
                  className="w-full mt-0.5 border border-gray-300 rounded-lg px-2 py-1.5 text-sm focus:ring-1 focus:ring-red-400 focus:outline-none disabled:bg-gray-100">
                  <option value="">Select Model</option>
                  {modelList.map(m => <option key={m.value} value={m.value}>{m.label}</option>)}
                </select>
              </div>

              {/* Year From */}
              <div>
                <label className="text-xs text-gray-500">Year From</label>
                <select value={row.yearFrom} onChange={e => updateRow(index, 'yearFrom', e.target.value)}
                  disabled={!row.model}
                  className="w-full mt-0.5 border border-gray-300 rounded-lg px-2 py-1.5 text-sm focus:ring-1 focus:ring-red-400 focus:outline-none disabled:bg-gray-100">
                  <option value="">Any Year</option>
                  {yearList.map(y => <option key={y.value} value={y.value}>{y.label}</option>)}
                </select>
              </div>

              {/* Year To */}
              <div>
                <label className="text-xs text-gray-500">Year To</label>
                <select value={row.yearTo} onChange={e => updateRow(index, 'yearTo', e.target.value)}
                  disabled={!row.model}
                  className="w-full mt-0.5 border border-gray-300 rounded-lg px-2 py-1.5 text-sm focus:ring-1 focus:ring-red-400 focus:outline-none disabled:bg-gray-100">
                  <option value="">Any Year</option>
                  {yearList.map(y => <option key={y.value} value={y.value}>{y.label}</option>)}
                </select>
              </div>

              {/* Variant */}
              <div>
                <label className="text-xs text-gray-500">Variant</label>
                <select value={row.variant} onChange={e => updateRow(index, 'variant', e.target.value)}
                  disabled={!row.model}
                  className="w-full mt-0.5 border border-gray-300 rounded-lg px-2 py-1.5 text-sm focus:ring-1 focus:ring-red-400 focus:outline-none disabled:bg-gray-100">
                  {variantList.length > 0
                    ? variantList.map(v => <option key={v.value} value={v.value}>{v.label}</option>)
                    : <option value="All">All Variants</option>
                  }
                </select>
              </div>

              {/* Fuel Type */}
              <div>
                <label className="text-xs text-gray-500">Fuel Type</label>
                <select value={row.fuelType} onChange={e => updateRow(index, 'fuelType', e.target.value)}
                  className="w-full mt-0.5 border border-gray-300 rounded-lg px-2 py-1.5 text-sm focus:ring-1 focus:ring-red-400 focus:outline-none">
                  {FUEL_TYPES.map(f => <option key={f} value={f}>{f}</option>)}
                </select>
              </div>
            </div>
          </div>
        );
      })}

      <button
        type="button"
        onClick={addRow}
        className="flex items-center gap-1 text-sm text-red-600 hover:text-red-700 font-medium border border-dashed border-red-300 rounded-lg px-3 py-2 hover:bg-red-50 transition w-full justify-center"
      >
        + Add Vehicle Compatibility
      </button>
    </div>
  );
};

export default VehicleCompatibilitySelector;
